import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fitLauncherService } from '@/api/services/fitLauncher';
import type { FitLauncherDeploymentCreateRequest, FitLauncherPlanRequest } from '@/api/types';

/**
 * Launcher keys live under their own root, not under `['projects']`, so the
 * many project invalidations (edits, refreshes, deletes) do not refetch them.
 * Mutations invalidate the whole root, which covers the user-wide list.
 */
export const fitLauncherRootKey = ['fit-launcher-deployments'] as const;

export const myFitLauncherDeploymentsKey = ['fit-launcher-deployments', 'mine'] as const;

export const fitLauncherDeploymentsKey = (projectId: string) =>
  ['fit-launcher-deployments', 'project', projectId] as const;

export const fitLauncherDeploymentKey = (projectId: string, deploymentId: string) =>
  ['fit-launcher-deployments', 'project', projectId, deploymentId] as const;

/** The launch check's key: the whole request, so each selection is its own
 *  entry and going back to an earlier one needs no new request. Outside the
 *  deployments root: creating or deleting a deployment does not change it. */
export const fitLauncherPlanKey = (body: FitLauncherPlanRequest | null) =>
  ['fit-launcher-plan', body] as const;

/**
 * Whether the server would accept a launch selection, each GPU's usage and
 * the models that still fit alongside it. Read-only: the endpoint creates
 * nothing. It re-runs the Fit and is rate limited, so a failure is not
 * retried; the previous answer stays shown while the next one loads.
 */
export function useFitLauncherPlan(body: FitLauncherPlanRequest | null) {
  return useQuery({
    queryKey: fitLauncherPlanKey(body),
    queryFn: () => fitLauncherService.plan(body as FitLauncherPlanRequest),
    enabled: !!body && body.models.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

export function useFitLauncherDeployments(projectId: string | undefined) {
  return useQuery({
    queryKey: fitLauncherDeploymentsKey(projectId ?? ''),
    queryFn: () => fitLauncherService.list(projectId as string),
    enabled: !!projectId,
  });
}

/** Every launcher deployment of the current user, in one request. */
export function useMyFitLauncherDeployments() {
  return useQuery({
    queryKey: myFitLauncherDeploymentsKey,
    queryFn: fitLauncherService.listMine,
    staleTime: 60_000,
  });
}

export function useFitLauncherDeployment(
  projectId: string | undefined,
  deploymentId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: fitLauncherDeploymentKey(projectId ?? '', deploymentId ?? ''),
    queryFn: () => fitLauncherService.detail(projectId as string, deploymentId as string),
    enabled: enabled && !!projectId && !!deploymentId,
    // Hardware reports and smoke results arrive from the host while the user
    // watches; a slow poll picks them up without a reload.
    refetchInterval: 60_000,
  });
}

export const fitLauncherReadmeKey = (projectId: string, deploymentId: string) =>
  ['fit-launcher-deployments', 'project', projectId, deploymentId, 'readme'] as const;

/**
 * The bundle's README, the run instructions. It is fixed when the bundle is
 * made, so it is fetched once, and only while `enabled` (the dialog is open).
 */
export function useFitLauncherReadme(
  projectId: string | undefined,
  deploymentId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: fitLauncherReadmeKey(projectId ?? '', deploymentId ?? ''),
    queryFn: () => fitLauncherService.readme(projectId as string, deploymentId as string),
    enabled: enabled && !!projectId && !!deploymentId,
    staleTime: Infinity,
  });
}

/**
 * Creates a launcher deployment. The response carries the ingestion token
 * once. `gcTime: 0` drops the finished mutation (and the token with it) from
 * the mutation cache as soon as nothing observes it, and the response is
 * never written to the query cache: callers hand the token to the token-once
 * dialog and nothing else.
 */
export function useCreateFitLauncherDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      body,
    }: {
      projectId: string;
      body: FitLauncherDeploymentCreateRequest;
    }) => fitLauncherService.create(projectId, body),
    gcTime: 0,
    onSuccess: () => qc.invalidateQueries({ queryKey: fitLauncherRootKey }),
  });
}

/** Rotates the ingestion token. Same token-once rules as create. */
export function useRotateFitLauncherToken(projectId: string, deploymentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fitLauncherService.rotateToken(projectId, deploymentId),
    gcTime: 0,
    onSuccess: () => qc.invalidateQueries({ queryKey: fitLauncherRootKey }),
  });
}

export function useRevokeFitLauncherToken(projectId: string, deploymentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fitLauncherService.revokeToken(projectId, deploymentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fitLauncherRootKey }),
  });
}

/** Deletes a deployment. Its detail query is removed first, so nothing
 *  refetches it (a 404) while the view goes away. */
export function useDeleteFitLauncherDeployment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => fitLauncherService.remove(projectId, deploymentId),
    onSuccess: (_data, deploymentId) => {
      qc.removeQueries({ queryKey: fitLauncherDeploymentKey(projectId, deploymentId), exact: true });
      return Promise.all([
        qc.invalidateQueries({ queryKey: fitLauncherDeploymentsKey(projectId), exact: true }),
        qc.invalidateQueries({ queryKey: myFitLauncherDeploymentsKey, exact: true }),
      ]);
    },
  });
}

/** A fresh presigned bundle URL. A mutation, not a query: the URL is a
 *  bearer credential for an hour and must not sit in the cache. */
export function useFitLauncherDownload(projectId: string, deploymentId: string) {
  return useMutation({
    mutationFn: () => fitLauncherService.downloadUrl(projectId, deploymentId),
    gcTime: 0,
  });
}
